import { useState, FormEvent } from 'react'
import { TCGSelector } from './TCGSelector'
import { SegmentSelector } from './SegmentSelector'
import { SocialSelector, SocialState } from './SocialSelector'
import { Field, TextInput, TextArea } from './Field'
import { SocialId, SegmentId, SOCIALS } from '../../constants/socials'

export type LeadFormValues = {
  tcgs: string[]
  name: string
  email: string
  phone: string // digits only
  facebook: boolean
  socials: Partial<Record<SocialId, string>>
  segments: SegmentId[]
  notes: string
}

const EMPTY_ENABLED: Record<SocialId, boolean> = {
  facebook: false,
  discord: false,
  reddit: false,
  twitter: false,
  instagram: false,
  tcgplayer: false,
  whatnot: false,
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  const len = digits.length
  if (len === 0) return ''
  if (len < 4) return `(${digits}`
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Props = {
  initial?: Partial<LeadFormValues>
  onSubmit: (values: LeadFormValues) => void | Promise<void>
  submitLabel?: string
  onCancel?: () => void
  // Internal-only fields hidden from the public capture form; surfaced on
  // the Friends edit view via these flags.
  showSegments?: boolean
  showNotes?: boolean
  // 'rail' (default) is the compact pill rail used in the Friends edit view.
  // 'stacked' shows every social as a switch row so first-time users on the
  // public form can discover the options without hunting.
  socialsVariant?: 'rail' | 'stacked'
  // 'labeled' (default) shows the game name below each TCG chip; 'logos'
  // drops the caption for a cleaner public capture row.
  tcgsVariant?: 'labeled' | 'logos'
  // Show the friendly intro line above the form (public capture only).
  intro?: string
}

export function LeadForm({
  initial,
  onSubmit,
  submitLabel = 'Save',
  onCancel,
  showSegments = false,
  showNotes = false,
  socialsVariant = 'rail',
  tcgsVariant = 'labeled',
  intro,
}: Props) {
  const [tcgs, setTcgs] = useState<string[]>(initial?.tcgs ?? [])
  const [name, setName] = useState(initial?.name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [phone, setPhone] = useState(
    initial?.phone ? formatPhone(initial.phone) : '',
  )
  const [social, setSocial] = useState<SocialState>(() => {
    const enabled: Record<SocialId, boolean> = {
      ...EMPTY_ENABLED,
      facebook: !!initial?.facebook,
    }
    const usernames: Partial<Record<SocialId, string>> = {}
    if (initial?.socials) {
      for (const [k, v] of Object.entries(initial.socials)) {
        if (v) {
          enabled[k as SocialId] = true
          usernames[k as SocialId] = v
        }
      }
    }
    return { enabled, usernames }
  })
  const [segments, setSegments] = useState<SegmentId[]>(initial?.segments ?? [])
  const [notes, setNotes] = useState(initial?.notes ?? '')

  // Validation surfaces only after the user has blurred a field once, so they
  // don't get yelled at mid-typing. After the first blur, errors update live
  // (so they see the green light when they fix it).
  const [emailTouched, setEmailTouched] = useState(false)
  const emailError =
    emailTouched && email && !EMAIL_RE.test(email)
      ? "That doesn't look right"
      : undefined

  const [busy, setBusy] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (email && !EMAIL_RE.test(email)) {
      setEmailTouched(true)
      return
    }
    setBusy(true)
    try {
      const values: LeadFormValues = {
        tcgs,
        name: name.trim(),
        email: email.trim(),
        phone: phone.replace(/\D/g, ''),
        facebook: !!social.enabled.facebook,
        socials: SOCIALS.filter(
          (s) => s.type === 'username' && social.enabled[s.id],
        ).reduce(
          (acc, s) => ({
            ...acc,
            [s.id]: (social.usernames[s.id] ?? '').trim(),
          }),
          {} as Record<string, string>,
        ),
        segments,
        notes: notes.trim(),
      }
      await onSubmit(values)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        {intro && (
          <p className="text-[14px] leading-snug text-theme-text-subtle">
            {intro}
          </p>
        )}

        <section className="space-y-2">
          <div className="text-sm font-medium text-theme-text-subtle">
            Which games do you play?
          </div>
          <TCGSelector
            value={tcgs}
            onChange={setTcgs}
            variant={tcgsVariant}
          />
        </section>

        <section className="space-y-3">
          <Field label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoCapitalize="words"
              autoComplete="name"
            />
          </Field>

          <Field label="Email" error={emailError}>
            <TextInput
              type="email"
              inputMode="email"
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="you@email.com"
              invalid={!!emailError}
            />
          </Field>

          <Field label="Phone">
            <TextInput
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(555) 123-4567"
            />
          </Field>
        </section>

        <section className="space-y-2">
          <div className="text-sm font-medium text-theme-text-subtle">
            Socials
          </div>
          <SocialSelector
            value={social}
            onChange={setSocial}
            variant={socialsVariant}
          />
        </section>

        {showSegments && (
          <section className="space-y-2">
            <div className="text-sm font-medium text-theme-text-subtle">
              Segments
            </div>
            <SegmentSelector value={segments} onChange={setSegments} />
          </section>
        )}

        {showNotes && (
          <section>
            <Field label="Notes">
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything worth remembering about this lead…"
              />
            </Field>
          </section>
        )}
      </div>

      <div
        className="sticky bottom-0 flex gap-2 border-t border-white/5 bg-theme-surface/95 px-5 pb-6 pt-3 backdrop-blur"
        style={{ paddingBottom: 'max(1.5rem, var(--safe-bottom))' }}
      >
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl bg-white/[0.04] px-4 py-3.5 text-base font-medium text-theme-text-subtle ring-1 ring-white/10"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="flex-1 rounded-2xl bg-primary-500 px-4 py-3.5 text-base font-semibold text-black transition-opacity disabled:opacity-60"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}
